import { Trash } from 'almanac-ui/icons'
import Styles from './PostImagesList.module.scss'
import { Button } from 'components/Button'
import { Text } from 'almanac-ui/Text/Text'
import { MediaObject } from 'types'
import { VideoDuration } from 'components/VideoDuration/VideoDuration'
import {
  ArticlePostPreviewActionTypes,
  useArticlePostPreviewContext,
} from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { useModals } from 'almanac-ui/Modal'
import { MediaFileViewerModal } from 'components/MediaFileViewerModal/MediaFileViewerModal'
import { ProgressBar } from './ProgressBar/ProgressBar'
import { useUpdateMediaObjectPosition } from 'hooks/queries/scheduledPosts/updateMediaObjectPositions'
import { memo, useEffect, useState } from 'react'
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'

interface PostImageItemProps {
  file: MediaObject
  onRemove: () => void
  readOnly?: boolean
}

interface PostImagesListProps {
  files: MediaObject[]
  onRemove: (index: number) => void
  addButton?: React.ReactNode
  readOnly?: boolean
}

export const PostImageItem = memo(function PostImageItem({ file, onRemove, readOnly }: PostImageItemProps) {
  const { openModal } = useModals()
  return (
    <div className={Styles.postImage} onClick={() => openModal(MediaFileViewerModal, { file })}>
      <img src={file.thumbnail_url || file.source_url} alt={file.id.toString()} />
      {file.duration != null && <VideoDuration duration={file.duration} />}
      {!readOnly && (
        <Button variant="secondaryGrey" size="sm" className={Styles.remove} onPress={onRemove} frontIcon={Trash} />
      )}
    </div>
  )
})

export const SortableItem = ({ id, children }: { id: number; children: React.ReactNode }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={Styles.draggable}>
      {children}
    </div>
  )
}

export const PostImagesList = ({ files, onRemove, addButton, readOnly }: PostImagesListProps) => {
  const {
    state: { uploadingMediaFilesMetadata, editing, status },
    dispatch,
  } = useArticlePostPreviewContext()

  const { scheduledPost } = useArticlePostPreviewContext()
  const { mutate } = useUpdateMediaObjectPosition({ scheduledPostId: scheduledPost?.id as number })

  // Initialize local state with the files prop
  const [localFiles, setLocalFiles] = useState(files.map((file) => file.id))

  useEffect(() => {
    if (files.length !== localFiles.length) {
      setLocalFiles(files.map((file) => file.id))
    }
  }, [files, localFiles.length])

  // Function to handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    // If dropped outside the list or at the same position, do nothing
    if (!over || active.id === over.id) return

    // Get indices of the dragged and the target items
    const oldIndex = localFiles.indexOf(active.id as number)
    const newIndex = localFiles.indexOf(over.id as number)

    // Optimistically update localFiles immediately
    const newFiles = arrayMove(localFiles, oldIndex, newIndex)
    setLocalFiles(newFiles)

    // Remap newFiles from file IDs back to the actual file objects
    const updatedFiles = newFiles.map((id) => files.find((file) => file.id === id)).filter(Boolean)

    // If editing and status is Autoscheduled, update files in memory
    if (editing && status?.value === PostStatusPropertyValue.AutoPostScheduled) {
      dispatch({
        type: ArticlePostPreviewActionTypes.SET_FILES,
        payload: updatedFiles as MediaObject[],
      })
      setLocalFiles(newFiles)
    } else {
      // Find moved item in the original files array
      const movedItem = files.find((file) => file.id === active.id)

      if (!movedItem) return

      // Update server-side position
      mutate(
        { id: movedItem.id, position: newIndex + 1 },
        {
          onError: () => {
            // Revert to the original order in case of an error
            setLocalFiles(files.map((file) => file.id))
          },
        },
      )
    }
  }

  return (
    <div className={Styles.container}>
      {!readOnly || (readOnly && files.length > 0 && <Text variant="label">Media</Text>)}
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={localFiles} disabled={readOnly}>
          <div className={Styles.postImagesList}>
            {localFiles.map((id, index) => {
              const file = files.find((file) => file.id === id)
              if (!file) return null
              return (
                <SortableItem key={file.id} id={file.id}>
                  <PostImageItem
                    key={`${file.id}-${index}`}
                    file={file}
                    onRemove={() => onRemove(index)}
                    readOnly={readOnly}
                  />
                </SortableItem>
              )
            })}
            {uploadingMediaFilesMetadata.map((metadata, index) => (
              <div className={Styles.postImage} key={`uploading-media-file-${index}`}>
                <img className={Styles.uploadPreview} src={metadata.thumbnail_url} alt="video frame" />
                <ProgressBar progress={metadata.progress || 0} />
              </div>
            ))}
            {addButton}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}
